import React, { useEffect, useState } from "react";
import { api, platformLabel } from "../../lib/api";
import { Plus, Pencil, Trash2, X, Upload, Minus, Sparkles, Copy, Download, FileUp, Search, ChevronUp, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const empty = {
  name: "", description: "", price: 0, original_price: null,
  category: "consoles", platform: "ps5", condition: "new",
  stock: 0, images: [], tags: [], featured: false, sony_authorised: false, release_date: "", priority: 0, is_preorder: false,
};

function sortItems(items, col, dir) {
  if (!col) return items;
  return [...items].sort((a, b) => {
    let av, bv;
    switch (col) {
      case "name":     av = a.name.toLowerCase(); bv = b.name.toLowerCase(); break;
      case "category": av = a.category; bv = b.category; break;
      case "platform": av = a.platform; bv = b.platform; break;
      case "condition":av = a.condition; bv = b.condition; break;
      case "priority": av = a.priority || 1; bv = b.priority || 1; break;
      case "price":    av = a.price; bv = b.price; break;
      case "stock":    av = a.stock; bv = b.stock; break;
      default: return 0;
    }
    if (av < bv) return dir === "asc" ? -1 : 1;
    if (av > bv) return dir === "asc" ? 1 : -1;
    return 0;
  });
}

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | empty | product
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [importingCsv, setImportingCsv] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = no search active
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const load = async () => {
    const r = await api.get("/admin/products/all");
    setItems(r.data);
  };

  useEffect(() => { load(); }, []);

  const onSearchChange = (e) => {
    const q = e.target.value;
    setSearch(q);
    if (!q.trim()) { setSearchResults(null); return; }
    const ql = q.trim().toLowerCase();
    const filtered = items.filter((p) =>
      p.name.toLowerCase().includes(ql) ||
      (p.category || "").toLowerCase().includes(ql) ||
      (p.platform || "").toLowerCase().includes(ql)
    );
    setSearchResults(filtered);
  };

  const onSortCol = (col) => {
    if (sortCol === col) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  const displayed = sortItems(searchResults !== null ? searchResults : items, sortCol, sortDir);

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = { ...editing };
      if (!payload.release_date) delete payload.release_date;
      if (payload.original_price === "" || payload.original_price === null) delete payload.original_price;
      payload.price = Number(payload.price);
      payload.stock = Number(payload.stock);
      if (payload.id) {
        await api.put(`/admin/products/${payload.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Deleted");
    await load();
  };

  const onAdjustStock = async (p, delta) => {
    try {
      const r = await api.patch(`/admin/products/${p.id}/stock`, null, { params: { delta } });
      setItems((prev) => prev.map((x) => x.id === p.id ? { ...x, stock: r.data.stock } : x));
    } catch {
      toast.error("Stock update failed");
    }
  };

  const onGenerateAI = async () => {
    if (!editing.name) return toast.error("Enter a product name first");
    setGenerating(true);
    try {
      const r = await api.post("/admin/products/generate-ai", {
        name: editing.name,
        category: editing.category,
        platform: editing.platform,
        condition: editing.condition,
        price: editing.price ? Number(editing.price) : null,
        original_price: editing.original_price ? Number(editing.original_price) : null,
      });
      setEditing((p) => ({
        ...p,
        description: r.data.description || p.description,
        tags: r.data.tags || p.tags,
      }));
      toast.success("AI description & tags generated");
    } catch (e) {
      const msg = e?.response?.data?.detail || "AI generation failed";
      toast.error(msg, { duration: 5000 });
    } finally {
      setGenerating(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data.url;
      setEditing((prev) => ({ ...prev, images: [...(prev.images || []), url] }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onExportCsv = async () => {
    setExportingCsv(true);
    try {
      const res = await api.get("/admin/products/export-csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "products_inventory_update.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Products exported");
    } catch {
      toast.error("Export failed");
    } finally {
      setExportingCsv(false);
    }
  };

  const onImportCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm("Import this CSV and update product pricing and quantity?")) {
      e.target.value = "";
      return;
    }

    setImportingCsv(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/products/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const parts = [];
      if (r.data.updated) parts.push(`Updated ${r.data.updated}`);
      if (r.data.created) parts.push(`Created ${r.data.created}`);
      if (r.data.skipped) parts.push(`Skipped ${r.data.skipped}`);
      toast.success(parts.join(", ") + " products");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.errors?.length) {
        toast.error(detail.errors.slice(0, 3).join(" "), { duration: 7000 });
      } else {
        toast.error(detail || "Import failed");
      }
    } finally {
      setImportingCsv(false);
      e.target.value = "";
    }
  };

  const onImportNewCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm("Create new hidden products from this CSV? (Name, Category, Condition, Quantity, Price columns required — Platform optional)")) {
      e.target.value = "";
      return;
    }

    setImportingCsv(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/products/import-new", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Created ${r.data.created} products${r.data.skipped ? `, skipped ${r.data.skipped}` : ""}`);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.errors?.length) {
        toast.error(detail.errors.slice(0, 3).join(" "), { duration: 7000 });
      } else {
        toast.error(detail || "Import failed");
      }
    } finally {
      setImportingCsv(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Catalog</div>
          <h1 className="text-3xl font-black uppercase tracking-tighter">Products</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onExportCsv} disabled={exportingCsv} className="border border-gold/30 text-gold font-extrabold uppercase tracking-wider text-xs px-4 py-2.5 rounded-full hover:bg-gold/10 disabled:opacity-50 flex items-center gap-2">
            <Download className="w-4 h-4" /> {exportingCsv ? "Exporting..." : "Export"}
          </button>
          <label className={`border border-gold/30 text-gold font-extrabold uppercase tracking-wider text-xs px-4 py-2.5 rounded-full hover:bg-gold/10 flex items-center gap-2 ${importingCsv ? "opacity-50 pointer-events-none" : "cursor-pointer"}`}>
            <FileUp className="w-4 h-4" /> {importingCsv ? "Importing..." : "Import"}
            <input type="file" accept=".csv,text/csv" onChange={onImportCsv} className="hidden" />
          </label>
          <label className={`border border-gold/30 text-gold font-extrabold uppercase tracking-wider text-xs px-4 py-2.5 rounded-full hover:bg-gold/10 flex items-center gap-2 ${importingCsv ? "opacity-50 pointer-events-none" : "cursor-pointer"}`} title="Create new hidden products from CSV (Name, Category, Condition, Quantity, Price)">
            <FileUp className="w-4 h-4" /> {importingCsv ? "Importing..." : "Import New"}
            <input type="file" accept=".csv,text/csv" onChange={onImportNewCsv} className="hidden" />
          </label>
          <button onClick={() => setEditing({ ...empty })} data-testid="new-product-btn" className="bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-xs px-5 py-2.5 rounded-full hover:bg-gold-light flex items-center gap-2">
            <Plus className="w-4 h-4" /> New Product
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            value={search}
            onChange={onSearchChange}
            placeholder="Search products..."
            className="w-full bg-navy-700 border border-gold/20 rounded-full pl-9 pr-8 py-2 text-sm focus:outline-none focus:border-gold/50"
          />
          {search && (
            <button onClick={() => { setSearch(""); setSearchResults(null); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <span className="text-xs text-slate-400 uppercase tracking-wider">
          {displayed.length} {searchResults !== null ? `result${displayed.length !== 1 ? "s" : ""}` : "products"}
        </span>
      </div>

      <div className="bg-navy-700 rounded-xl neon-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-navy-800 text-[10px] uppercase tracking-[0.2em] text-gold/80">
            <tr>
              {[
                { col: "name",     label: "Product",  align: "left"  },
                { col: "category", label: "Category", align: "left"  },
                { col: "platform", label: "Platform", align: "left"  },
                { col: "condition",label: "Cond.",    align: "left"  },
                { col: "priority", label: "Priority", align: "right" },
                { col: "price",    label: "Price",    align: "right" },
                { col: "stock",    label: "Stock",    align: "right" },
              ].map(({ col, label, align }) => (
                <th key={col} className={`p-4 text-${align}`}>
                  <button
                    onClick={() => onSortCol(col)}
                    className={`inline-flex items-center gap-1 hover:text-gold transition ${sortCol === col ? "text-gold" : ""}`}
                  >
                    {label}
                    <span className="flex flex-col leading-none">
                      <ChevronUp   className={`w-2.5 h-2.5 -mb-0.5 ${sortCol === col && sortDir === "asc"  ? "text-gold" : "text-slate-600"}`} />
                      <ChevronDown className={`w-2.5 h-2.5 ${sortCol === col && sortDir === "desc" ? "text-gold" : "text-slate-600"}`} />
                    </span>
                  </button>
                </th>
              ))}
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {displayed.map((p) => (
              <tr key={p.id} className="border-t border-gold/10 hover:bg-navy-800/50">
                <td className="p-4 flex items-center gap-3">
                  {p.images?.[0] && <img src={p.images[0]} className="w-10 h-10 object-cover rounded" alt="" />}
                  <div className="min-w-0">
                    <Link to={`/product/${p.id}`} target="_blank" rel="noreferrer" className="font-semibold hover:text-gold transition truncate block">
                      {p.name}
                    </Link>
                    <button
                      onClick={() => { navigator.clipboard.writeText(p.id); toast.success("UUID copied"); }}
                      className="flex items-center gap-1 text-[10px] font-mono text-slate-500 hover:text-gold transition mt-0.5"
                      title="Copy product UUID"
                    >
                      <Copy className="w-2.5 h-2.5" />
                      {p.id.slice(0, 8)}…
                    </button>
                  </div>
                </td>
                <td className="p-4 capitalize">{p.category}</td>
                <td className="p-4">{platformLabel(p.platform)}</td>
                <td className="p-4 uppercase text-xs">{p.condition}</td>
                <td className="p-4 text-right">
                  {p.priority === 0
                    ? <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-black bg-slate-700 text-slate-500 border border-slate-600" title="Hidden — not shown on website">H</span>
                    : <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-black ${p.priority >= 4 ? "bg-gold text-navy-900" : p.priority === 3 ? "bg-gold/30 text-gold" : "bg-navy-600 text-slate-400"}`}>{p.priority}</span>
                  }
                </td>
                <td className="p-4 text-right font-bold text-gold">₹{p.price.toLocaleString()}</td>
                <td className="p-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => onAdjustStock(p, -1)} disabled={p.stock === 0} className="w-6 h-6 flex items-center justify-center rounded bg-navy-600 hover:bg-rose-500/30 text-slate-300 hover:text-rose-400 disabled:opacity-30 transition"><Minus className="w-3 h-3" /></button>
                    <span className="w-8 text-center font-bold tabular-nums">{p.stock}</span>
                    <button onClick={() => onAdjustStock(p, 1)} className="w-6 h-6 flex items-center justify-center rounded bg-navy-600 hover:bg-emerald-500/30 text-slate-300 hover:text-emerald-400 transition"><Plus className="w-3 h-3" /></button>
                  </div>
                </td>
                <td className="p-4 text-right">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setEditing(p)} className="p-1.5 text-slate-300 hover:text-gold" data-testid={`edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => onDelete(p)} className="p-1.5 text-slate-300 hover:text-rose-400" data-testid={`delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {displayed.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-slate-400">
                {searchResults !== null ? `No results for "${search}"` : "No products yet."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-navy-900/80 backdrop-blur flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-navy-800 rounded-2xl p-6 max-w-2xl w-full neon-border max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-black uppercase tracking-tight">{editing.id ? "Edit" : "New"} Product</h2>
              <button onClick={() => setEditing(null)} className="p-1 text-slate-400 hover:text-gold"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input full label="Name" value={editing.name} onChange={(v) => setEditing((p) => ({ ...p, name: v }))} />
              <div className="col-span-2">
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Tags</span>
                <div className="flex flex-wrap gap-1.5 mt-1.5 mb-1.5">
                  {(editing.tags || []).map((tag, i) => (
                    <span key={i} className="flex items-center gap-1 bg-navy-600 border border-gold/20 text-xs px-2 py-0.5 rounded-full">
                      {tag}
                      <button type="button" onClick={() => setEditing((p) => ({ ...p, tags: p.tags.filter((_, idx) => idx !== i) }))} className="text-slate-400 hover:text-rose-400"><X className="w-2.5 h-2.5" /></button>
                    </span>
                  ))}
                </div>
                <input
                  placeholder="Add tag and press Enter"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && tagInput.trim()) {
                      e.preventDefault();
                      const t = tagInput.trim().toLowerCase();
                      if (!(editing.tags || []).includes(t)) {
                        setEditing((p) => ({ ...p, tags: [...(p.tags || []), t] }));
                      }
                      setTagInput("");
                    }
                  }}
                  className="w-full bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-xs focus:outline-none focus:border-gold/60"
                />
              </div>
              <Input label="Price (₹)" type="number" value={editing.price} onChange={(v) => setEditing((p) => ({ ...p, price: v }))} />
              <Input label="Original Price (optional)" type="number" value={editing.original_price ?? ""} onChange={(v) => setEditing((p) => ({ ...p, original_price: v === "" ? null : v }))} />
              <Select label="Category" value={editing.category} onChange={(v) => setEditing((p) => ({ ...p, category: v }))} options={[
                { value: "consoles", label: "Consoles" },
                { value: "games", label: "Games" },
                { value: "accessories", label: "Accessories" },
                { value: "controllers", label: "Controllers" },
                { value: "digital", label: "Digital" },
              ]} />
              <Select label="Platform" value={editing.platform} onChange={(v) => setEditing((p) => ({ ...p, platform: v }))} options={[
                { value: "ps5", label: "PS5" },
                { value: "ps4", label: "PS4" },
                { value: "switch 2", label: "Switch 2" },
                { value: "switch 1", label: "Switch 1" },
                { value: "xbox", label: "Xbox" },
              ]} />
              <Select label="Condition" value={editing.condition} onChange={(v) => setEditing((p) => ({ ...p, condition: v }))} options={["new", "used"]} />
              <div>
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 block mb-1">Priority</span>
                <div className="flex gap-1 mt-1">
                  {[0, 1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setEditing((p) => ({ ...p, priority: n }))}
                      className={`flex-1 py-2 rounded-md text-sm font-bold border transition ${
                        editing.priority === n
                          ? n === 0 ? "bg-slate-600 text-slate-300 border-slate-500" : "bg-gold text-navy-900 border-gold"
                          : "bg-navy-700 border-gold/20 text-slate-300 hover:border-gold/50"
                      }`}
                    >
                      {n === 0 ? "H" : n}
                    </button>
                  ))}
                </div>
                <div className="text-[9px] text-slate-500 mt-1">H = hidden · 1–5 visible (5 shown first)</div>
              </div>
              <Input label="Stock" type="number" value={editing.stock} onChange={(v) => setEditing((p) => ({ ...p, stock: v }))} />
              <DateInput label="Release Date" value={editing.release_date || ""} onChange={(v) => setEditing((p) => ({ ...p, release_date: v }))} />
              <div className="col-span-2 flex gap-4">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.featured} onChange={(e) => setEditing((p) => ({ ...p, featured: e.target.checked }))} className="accent-gold" /> Featured</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.sony_authorised} onChange={(e) => setEditing((p) => ({ ...p, sony_authorised: e.target.checked }))} className="accent-gold" /> Sony Authorised</label>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">Images</div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(editing.images || []).map((url, i) => (
                    <div key={i} className="relative w-20 h-20 rounded overflow-hidden">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setEditing((p) => ({ ...p, images: p.images.filter((_, idx) => idx !== i) }))} className="absolute top-0.5 right-0.5 bg-rose-500 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <label className="flex-1 cursor-pointer flex items-center gap-2 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-xs hover:border-gold/50">
                    <Upload className="w-4 h-4 text-gold" /> {uploading ? "Uploading..." : "Upload image"}
                    <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
                  </label>
                  <input
                    placeholder="or paste image URL"
                    disabled={uploading}
                    onKeyDown={async (e) => {
                      const url = e.currentTarget.value.trim();
                      if (e.key !== "Enter" || !url) return;
                      e.currentTarget.value = "";
                      setUploading(true);
                      try {
                        const r = await api.post("/admin/upload-from-url", { url });
                        setEditing((prev) => ({ ...prev, images: [...(prev.images || []), r.data.url] }));
                        toast.success("Image saved to CDN");
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Could not fetch that URL");
                      } finally {
                        setUploading(false);
                      }
                    }}
                    className="flex-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-xs focus:outline-none focus:border-gold/60 disabled:opacity-50"
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Description</span>
                <button
                  type="button"
                  onClick={onGenerateAI}
                  disabled={generating}
                  className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-3 py-1 rounded-full border border-gold/30 text-gold hover:bg-gold/10 disabled:opacity-50 transition"
                >
                  <Sparkles className="w-3 h-3" />
                  {generating ? "Generating..." : "Generate with AI"}
                </button>
              </div>
              <textarea
                value={editing.description}
                onChange={(e) => setEditing((p) => ({ ...p, description: e.target.value }))}
                rows={14}
                maxLength={8000}
                placeholder={"Supports markdown:\n## Heading\n**bold text**\n- bullet item"}
                className="w-full bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60 resize-y font-mono"
              />
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="flex-1 border border-gold/30 text-slate-300 font-bold uppercase tracking-wider text-xs py-3 rounded-full hover:bg-navy-700">Cancel</button>
              <button onClick={onSave} disabled={saving || !editing.name} data-testid="save-product-btn" className="flex-1 bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-xs py-3 rounded-full hover:bg-gold-light disabled:opacity-50">{saving ? "Saving..." : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// value/onChange use YYYY-MM-DD; displays and accepts DD-MM-YYYY
function DateInput({ label, value, onChange }) {
  const toDisplay = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    if (y && m && d) return `${d}-${m}-${y}`;
    return iso;
  };
  const [display, setDisplay] = React.useState(() => toDisplay(value));
  React.useEffect(() => { setDisplay(toDisplay(value)); }, [value]);

  const handle = (e) => {
    let raw = e.target.value.replace(/[^\d]/g, "").slice(0, 8);
    let formatted = raw;
    if (raw.length > 4) formatted = raw.slice(0, 2) + "-" + raw.slice(2, 4) + "-" + raw.slice(4);
    else if (raw.length > 2) formatted = raw.slice(0, 2) + "-" + raw.slice(2);
    setDisplay(formatted);
    if (raw.length === 8) {
      onChange(`${raw.slice(4)}-${raw.slice(2, 4)}-${raw.slice(0, 2)}`);
    } else {
      onChange("");
    }
  };

  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">{label}</span>
      <input
        value={display}
        onChange={handle}
        placeholder="DD-MM-YYYY"
        className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60"
      />
    </label>
  );
}

function Input({ label, value, onChange, type = "text", textarea = false, full = false }) {
  return (
    <label className={`block ${full ? "col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">{label}</span>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60" />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60" />
      )}
    </label>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60">
        {options.map((o) => {
          const val = typeof o === "string" ? o : o.value;
          const lbl = typeof o === "string" ? o : o.label;
          return <option key={val} value={val}>{lbl}</option>;
        })}
      </select>
    </label>
  );
}
